<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor", { default: "Journal" }),
  title: $.text("Heading"),
  specialHeading: $.checkbox("Use special heading font", { default: true }).if(
    "title pr",
  ),
  intro: $.text("Journal introduction", { multiline: true }),
  document: $.upload("PDF journal", { accept: "application/pdf,.pdf" }),
  direction: $.select(
    "Reading direction",
    [
      { label: "Left to right", value: "ltr" },
      { label: "Right to left", value: "rtl" },
    ],
    { default: "ltr" },
  ),
  layout: $.select(
    "Page display",
    [
      { label: "First page alone", value: "single" },
      { label: "First page in a spread", value: "spread" },
    ],
    { default: "single" },
  ),
  sizeMode: $.select(
    "Page proportion",
    [
      { label: "Detect automatically", value: "auto" },
      { label: "Set manually", value: "manual" },
    ],
    { default: "auto" },
  ),
  pageWidth: $.number("Page proportion — width", {
    default: "816",
    min: 1,
  }).if('sizeMode eq "manual"'),
  pageHeight: $.number("Page proportion — height", {
    default: "1056",
    min: 1,
  }).if('sizeMode eq "manual"'),
  showNavigation: $.checkbox("Show navigation arrows", { default: true }),
  showPageCounter: $.checkbox("Show page numbers", { default: true }),
  showZoom: $.checkbox("Show zoom controls", { default: true }),
  showFullscreen: $.checkbox("Show fullscreen button", { default: true }),
  surface: $.select(
    "Background",
    [
      { label: "Page background", value: "page" },
      { label: "White surface", value: "surface" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "page" },
  ),
  backgroundImage: $.upload("Background image").if('surface eq "image"'),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if('surface eq "image" and backgroundImage pr'),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    'surface eq "image" and backgroundImage pr',
  ),
});
export const EventJournal = defineBlock({
  name: "EventJournal",
  label: "Digital journal",
  icon: "menu_book",
  thumbnail: "/block-thumbs/EventJournal.svg",
  category: "Event",
  order: 90,
  schema,
})
</script>
<script setup lang="ts">
import { computed } from "vue";
import { useBloxRuntime } from "@bagelink/blox"
import { EmptyState, FlipBook, pathKeyToURL } from "@bagelink/vue"


;

const props = withDefaults(
  defineProps<{
    anchor?: string;
    title?: string;
    specialHeading?: boolean;
    intro?: string;
    document?: string;
    direction?: "ltr" | "rtl";
    layout?: "single" | "spread";
    sizeMode?: "auto" | "manual";
    pageWidth?: string | number;
    pageHeight?: string | number;
    showNavigation?: boolean;
    showPageCounter?: boolean;
    showZoom?: boolean;
    showFullscreen?: boolean;
    surface?: "page" | "surface" | "brand" | "dark" | "image";
    backgroundImage?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    dimBackground?: boolean;
  }>(),
  {
    anchor: "Journal",
    title: "",
    specialHeading: true,
    intro: "",
    document: "",
    direction: "ltr",
    layout: "single",
    sizeMode: "auto",
    pageWidth: 816,
    pageHeight: 1056,
    showNavigation: true,
    showPageCounter: true,
    showZoom: true,
    showFullscreen: true,
    surface: "page",
    backgroundImage: "",
    backgroundFit: "cover",
    dimBackground: false,
  },
);

const { isPreview } = useBloxRuntime();
const surfaceClass = computed(
  () =>
    ({
      page: "bg-bg",
      surface: "bg-box",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "color-white keep-white",
    })[props.surface],
);
const sectionStyle = computed(() =>
  props.surface === "image" && props.backgroundImage
    ? {
        backgroundImage: `${props.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize:
          props.backgroundFit === "fill" ? "100% 100%" : props.backgroundFit,
      }
    : undefined,
);
const documentUrl = computed(() => pathKeyToURL(props.document));
const pageWidthValue = computed(() => Math.max(1, Number(props.pageWidth) || 816));
const pageHeightValue = computed(() => Math.max(1, Number(props.pageHeight) || 1056));
</script>

<template>
  <section
    v-if="documentUrl || isPreview"
    :id="anchor || undefined"
    class="px-2 py-5 m_px-1 m_py-2"
    :class="surfaceClass"
    :style="sectionStyle"
  >
    <div class="w-1000px txt-center">
      <h2
        v-if="title"
        class="m-0 txt-48 m_txt-32 line-height-11 m_line-height-12 balance"
        :class="{ 'blox-special-heading': specialHeading }"
      >
        {{ title }}
      </h2>
      <p v-if="intro" class="w-700px pretty pb-2">{{ intro }}</p>

      <EmptyState
        v-if="!documentUrl"
        icon="menu_book"
        title="Digital journal"
        description="Upload one PDF to preview this journal."
        class="bg-box border radius-2"
      />

      <FlipBook
        v-else
        :pdf="documentUrl"
        :direction
        :layout
        :size-mode="sizeMode"
        :page-width="pageWidthValue"
        :page-height="pageHeightValue"
        :navigation="showNavigation"
        :page-counter="showPageCounter"
        :zoom-controls="showZoom"
        :fullscreen="showFullscreen"
      />
    </div>
  </section>
</template>
